import React from "react";
import { Box, Container, Typography } from "@mui/material";
import { motion } from "framer-motion";
import BRAND from "../theme/talktokam";

const PrivacyPolicy = () => {
  return (
    <Box sx={{ background: "#050816", minHeight: "100vh" }}>
      <Box
        sx={{
          position: "relative",
          textAlign: "center",
          py: { xs: 10, md: 14 },
          overflow: "hidden",
          background: `radial-gradient(ellipse 800px 600px at 20% 10%, ${BRAND.cyan}33 0%, transparent 60%), radial-gradient(ellipse 700px 500px at 85% 90%, ${BRAND.purple}33 0%, transparent 60%), radial-gradient(ellipse 600px 400px at 50% 50%, ${BRAND.primary}1a 0%, transparent 60%), linear-gradient(180deg, #050816 0%, #0a0e27 50%, #050816 100%)`,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `linear-gradient(rgba(56,189,248,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(56,189,248,0.04) 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
            maskImage:
              "radial-gradient(ellipse at center, black 30%, transparent 70%)",
            WebkitMaskImage:
              "radial-gradient(ellipse at center, black 30%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <motion.div
          animate={{ y: [0, -40, 0], scale: [1, 1.1, 1] }}
          transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute",
            top: "5%",
            left: "-5%",
            width: 500,
            height: 500,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${BRAND.cyan}40 0%, transparent 70%)`,
            filter: "blur(60px)",
            pointerEvents: "none",
          }}
        />
        <motion.div
          animate={{ y: [0, 40, 0], scale: [1, 1.15, 1] }}
          transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute",
            bottom: "-10%",
            right: "-10%",
            width: 600,
            height: 600,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${BRAND.purple}40 0%, transparent 70%)`,
            filter: "blur(80px)",
            pointerEvents: "none",
          }}
        />
        <motion.div
          animate={{ y: [0, -25, 0], x: [0, 20, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute",
            top: "50%",
            right: "30%",
            width: 300,
            height: 300,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${BRAND.primary}26 0%, transparent 70%)`,
            filter: "blur(40px)",
            pointerEvents: "none",
          }}
        />
        <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: "2rem", md: "3rem" },
                background: "linear-gradient(135deg, #38bdf8, #a855f7)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                mb: 2,
              }}
            >
              Privacy Policy
            </Typography>
            <Typography sx={{ fontSize: "1.1rem", color: "#94a3b8" }}>
              Last updated: June 2026
            </Typography>
          </motion.div>
        </Container>
      </Box>

      <Box
        sx={{
          background: "linear-gradient(180deg, #0a0e27 0%, #050816 100%)",
          py: { xs: 6, md: 10 },
        }}
      >
        <Container sx={{ maxWidth: 800 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Box
              sx={{
                "& h3": {
                  fontWeight: 700,
                  fontSize: "1.2rem",
                  color: "#fff",
                  mt: 4,
                  mb: 1.5,
                },
                "& p": {
                  fontSize: "0.95rem",
                  color: "#94a3b8",
                  lineHeight: 1.8,
                  mb: 2,
                },
                "& ul": { pl: 2.5, mb: 2 },
                "& li": { fontSize: "0.9rem", color: "#94a3b8", mb: 0.5 },
              }}
            >
              <h3>1. Information We Collect</h3>
              <p>
                We collect information you provide directly to us when you
                create an account, configure your chatbot, or contact our
                support team. This includes your name, email address, company
                name, and billing information.
              </p>
              <p>
                We also automatically collect certain technical information when
                you use our service, including IP address, browser type, device
                information, and usage data (pages visited, features used,
                session duration).
              </p>
              <p>
                When your chatbot interacts with visitors, we collect and store
                conversation data, visitor messages, lead information (name,
                email, phone), and chatbot responses to provide the service.
              </p>

              <h3>2. How We Use Your Information</h3>
              <p>We use the information we collect to:</p>
              <ul>
                <li>Provide, maintain, and improve our chatbot platform</li>
                <li>Process transactions and manage your account</li>
                <li>Send technical notices, updates, and support messages</li>
                <li>Respond to your comments, questions, and requests</li>
                <li>Monitor and analyze trends, usage, and activities</li>
                <li>
                  Detect, investigate, and prevent fraudulent transactions and
                  abuse
                </li>
              </ul>

              <h3>3. Data Sharing</h3>
              <p>
                We do not sell your personal information to third parties. We
                may share your information with trusted service providers who
                help us operate our platform (payment processors, cloud
                infrastructure providers, email services), under strict data
                processing agreements.
              </p>

              <h3>4. Data Retention</h3>
              <p>
                We retain your account information for as long as your account
                is active. Conversation data is retained according to your
                plan's data retention policy. You can request deletion of your
                data at any time by contacting us.
              </p>

              <h3>5. Your Rights</h3>
              <p>
                Depending on your jurisdiction, you may have the right to
                access, correct, delete, or port your personal data. You may
                also have the right to restrict or object to certain processing
                activities. To exercise these rights, contact us at
                support@talktokam.com.
              </p>

              <h3>6. Data Security</h3>
              <p>
                We implement industry-standard security measures including
                encryption in transit (TLS 1.3) and at rest, access controls,
                and regular security audits to protect your data.
              </p>

              <h3>7. Cookies</h3>
              <p>
                We use cookies and similar tracking technologies to enhance your
                experience, analyze usage, and provide authentication. You can
                control cookie preferences through your browser settings. See
                our Cookie Policy for more details.
              </p>

              <h3>8. Instagram Data</h3>
              <p>
                When you connect your Instagram Business or Creator account to
                Talktokam, we access certain data through Meta's Instagram Graph
                API with your explicit permission (<code>instagram_manage_messages</code>).
              </p>
              <p>
                The data we access includes:
              </p>
              <ul>
                <li>Incoming Instagram Direct Messages (message content, sender ID, timestamps)</li>
                <li>Conversation metadata (conversation ID, participant information)</li>
                <li>Instagram Business Account information (username, profile picture, ID)</li>
              </ul>
              <p>
                We use this data solely to provide the following features:
              </p>
              <ul>
                <li>Automated replies to incoming DMs (Auto DM)</li>
                <li>AI-powered conversational responses</li>
                <li>Conversation history management within your account</li>
              </ul>
              <p>
                We do not sell your Instagram message data. We do not share it
                with third parties except as necessary to process your requests
                through Meta's APIs. Message data is stored securely and is only
                accessible to you and our systems for the purpose of providing
                the service.
              </p>
              <p>
                You may request deletion of your Instagram message data at any
                time by disconnecting your Instagram account from our platform
                or by contacting us at <code>support@talktokam.com</code>.
                Upon disconnection, stored message data will be deleted within
                30 days.
              </p>

              <h3>9. Contact</h3>
              <p>
                If you have questions about this Privacy Policy, please contact
                us at support@talktokam.com or through our Contact page.
              </p>
            </Box>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default PrivacyPolicy;
